import { MouseEvent, memo, useId, useMemo, useRef, useState } from 'react';
import React from 'react';

import { CompositeFilterDescriptor, FilterDescriptor, State, toDataSourceRequestString } from '@progress/kendo-data-query';
import type { Identifier, XYCoord } from 'dnd-core';
import { useAccordionButton } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';

import { TagModel, WorklistViewModel } from 'models';

import { useEvent } from 'core/hooks';
import { Accordion, Button, ButtonVariants } from 'core/ui';

import { useColumnsGridState } from 'features/exam/hooks';
import { ExamsGridService } from 'features/exam/services/exam-grid-service';
import { TagsField } from 'features/tags/fragments/TagsField';
import { useTagLookups } from 'features/tags/hooks/useTagLookups';

import CollapseIcon from '../assets/collapse.svg?react';
import ExpandIcon from '../assets/expand.svg?react';
import OriginalHandleIcon from '../assets/handle.svg?react';
import { WorklistEditService } from '../services';
import { WorklistFilterAccordionItemProps } from '../types';
import { WorklistFilterAccordionItem } from './WorklistFilterAccordionItem';
import { WorklistNameInput } from './WorklistNameInput';

const ALL_GRID_COLUMN_DEFINITIONS = ExamsGridService.getGridColumns();

type DropIndicator = 'above' | 'below' | 'on' | 'none';

interface DraggableWorklistItem extends WorklistViewModel {
  originalIndexInAllWorklists: number;
}

interface WorklistTagFilterAccordionItemProps extends Omit<WorklistFilterAccordionItemProps, 'filterType'> {
  dndType: string;
  nestingLevel?: number;
}

export const WorklistTagFilterAccordionItem = memo<WorklistTagFilterAccordionItemProps>(
  ({
    worklist,
    allWorklists,
    isTopLevel = false,
    currentDataState,
    currentColumnsState,
    onCreateWorklist,
    onDeleteWorklist,
    onUpdateWorklist,
    patientId,
    dndType,
    nestingLevel = 0,
  }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);

    const [worklistName, setWorklistName] = useState(worklist.name);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
    const [isItemsExpanded, setIsItemsExpanded] = useState(false);
    const [filterForm, setFilterForm] = useState(() => WorklistEditService.initializeWorklistEditModalDataState(worklist.dataState));
    const { columnsState: columnsStateForm, setColumnsState: setColumnsStateForm } = useColumnsGridState(worklist.columnState);

    const [fieldSelectionValue, setFieldSelectionValue] = useState<TagModel[]>([]);
    const { allHierarchicalTags, allFlatTags, isLoadingTags, tagIdToTagMap } = useTagLookups();

    const childWorklists = useMemo(
      () => allWorklists.filter((w) => w.parentViewId === worklist.id).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
      [allWorklists, worklist],
    );

    const [{ isDragging }, drag, preview] = useDrag({
      type: dndType,
      item: (): DraggableWorklistItem => {
        console.log('[WorklistTagFilterAccordionItem] useDrag ITEM factory triggered', { worklistId: worklist.id });
        return {
          ...worklist,
          originalIndexInAllWorklists: allWorklists.findIndex((w) => w.id === worklist.id),
        };
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [{ isOver, dropIndicator }, drop] = useDrop<DraggableWorklistItem, undefined, { isOver: boolean; dropIndicator: DropIndicator }>({
      accept: dndType,
      collect(monitor) {
        let indicator: DropIndicator = 'none';
        if (monitor.isOver({ shallow: true })) {
          const clientOffset = monitor.getClientOffset();
          const hoverBoundingRect = headerRef.current?.getBoundingClientRect();

          if (clientOffset && hoverBoundingRect) {
            const dropZoneRatio = (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height;
            if (dropZoneRatio > 0.25 && dropZoneRatio < 0.75) {
              indicator = 'on';
            } else if (dropZoneRatio <= 0.25) {
              indicator = 'above';
            } else {
              indicator = 'below';
            }
          }
        }
        return {
          isOver: monitor.isOver({ shallow: true }),
          dropIndicator: indicator,
        };
      },
      hover(draggedItem: DraggableWorklistItem, monitor) {
        if (!ref.current || draggedItem.id === worklist.id) return;
      },
      drop(draggedItem: DraggableWorklistItem, monitor) {
        console.log('[WorklistTagFilterAccordionItem] DROP triggered', { draggedId: draggedItem.id, targetId: worklist.id });
        if (!ref.current || !headerRef.current || draggedItem.id === worklist.id) return;

        const dropTargetItem = worklist;
        const mutableAllWorklists = [...allWorklists];
        const itemsToUpdateMap = new Map<number, WorklistViewModel>();

        const recordUpdate = (itemToUpdate: Partial<WorklistViewModel> & { id: number }) => {
          const existingItemInAll = mutableAllWorklists.find((w) => w.id === itemToUpdate.id)!; // Keep ! here if find can be undefined, or handle undefined case
          const existingItemInMap = itemsToUpdateMap.get(itemToUpdate.id);
          itemsToUpdateMap.set(itemToUpdate.id, { ...existingItemInAll, ...existingItemInMap, ...itemToUpdate });
        };

        const reordinalizeChildrenOf = (parentId: number | null) => {
          const children = mutableAllWorklists
            .map((w) => itemsToUpdateMap.get(w.id) || w) // Get latest state of items
            .filter((w) => w.parentViewId === parentId)
            .sort((a, b) => {
              const ordA = a.ordinal ?? Infinity;
              const ordB = b.ordinal ?? Infinity;
              if (ordA === ordB) return (a.name || '').localeCompare(b.name || '');
              return ordA - ordB;
            });

          children.forEach((child, index) => {
            const currentChildState = itemsToUpdateMap.get(child.id) || child;
            if (currentChildState.ordinal !== index || currentChildState.parentViewId !== parentId) {
              recordUpdate({ ...currentChildState, id: child.id, ordinal: index, parentViewId: parentId });
            }
          });
        };

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) {
          console.error('Could not get client offset on drop. Aborting.');
          return;
        }

        const hoverBoundingRect = headerRef.current.getBoundingClientRect();
        const dropZoneRatio = (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height;

        const draggedItemCurrentState = itemsToUpdateMap.get(draggedItem.id) || mutableAllWorklists.find((w) => w.id === draggedItem.id)!;

        // Prevent dragging a parent onto one of its own descendants
        let tempParent = dropTargetItem.parentViewId;
        while (tempParent !== null) {
          if (tempParent === draggedItemCurrentState.id) return;
          const parentWorklist = mutableAllWorklists.find((w) => w.id === tempParent);
          tempParent = parentWorklist ? parentWorklist.parentViewId : null;
        }

        // Determine Action based on drop zone
        if (
          dropZoneRatio > 0.25 &&
          dropZoneRatio < 0.75 &&
          draggedItemCurrentState.parentViewId !== dropTargetItem.id &&
          draggedItemCurrentState.id !== dropTargetItem.parentViewId
        ) {
          const oldParentId = draggedItemCurrentState.parentViewId;
          recordUpdate({ id: draggedItemCurrentState.id, parentViewId: dropTargetItem.id, ordinal: 100000 });

          reordinalizeChildrenOf(dropTargetItem.id);
          if (oldParentId !== dropTargetItem.id) {
            reordinalizeChildrenOf(oldParentId);
          }
        } else {
          // --- Action: Reorder Siblings (or move to a new sibling group) ---
          const targetParentId = dropTargetItem.parentViewId;
          const originalDraggedItemParentId = draggedItemCurrentState.parentViewId;

          if (originalDraggedItemParentId !== targetParentId) {
            recordUpdate({ id: draggedItemCurrentState.id, parentViewId: targetParentId });
          }

          const tempTargetOrdinal = itemsToUpdateMap.get(dropTargetItem.id)?.ordinal ?? dropTargetItem.ordinal ?? 0;
          if (dropZoneRatio <= 0.5) {
            recordUpdate({ id: draggedItemCurrentState.id, ordinal: tempTargetOrdinal - 0.5 });
          } else {
            recordUpdate({ id: draggedItemCurrentState.id, ordinal: tempTargetOrdinal + 0.5 });
          }
          reordinalizeChildrenOf(targetParentId);

          if (originalDraggedItemParentId !== targetParentId) {
            reordinalizeChildrenOf(originalDraggedItemParentId);
          }
        }

        const finalUpdatedWorklists = Array.from(itemsToUpdateMap.values());
        if (finalUpdatedWorklists.length > 0) {
          onUpdateWorklist(finalUpdatedWorklists);
        }
      },
    });

    drop(ref);
    preview(ref);

    const toggleFiltersAccordionClick = useAccordionButton(`${worklist.id}-filters`, () => {
      console.log('[WorklistTagFilterAccordionItem] Toggle filters. Worklist:', worklist);
      if (!isFiltersExpanded) {
        const tagIdMatch = worklist.oDataResourceString?.match(/TagId~in~([\d,]+)/);
        if (tagIdMatch && allFlatTags.length > 0) {
          const tagIds = tagIdMatch[1]
            .split(',')
            .map(Number)
            .filter((id) => !isNaN(id));
          const initialSelectedTagModels = tagIds.map((id) => tagIdToTagMap.get(id)).filter((tag): tag is TagModel => tag !== undefined);
          setFieldSelectionValue(initialSelectedTagModels);
          console.log('[WorklistTagFilterAccordionItem] Initialized fieldSelectionValue from worklist:', initialSelectedTagModels);
        } else {
          setFieldSelectionValue([]);
        }
      }
      setIsFiltersExpanded(!isFiltersExpanded);
    });

    const toggleItemsAccordionClick = useAccordionButton(`${worklist.id}-items`, () => setIsItemsExpanded(!isItemsExpanded));

    const handleSaveClick = useEvent((event: MouseEvent<HTMLButtonElement>) => {
      console.log('[WorklistTagFilterAccordionItem] handleSaveClick initiated.');
      console.log('[WorklistTagFilterAccordionItem] Current fieldSelectionValue (TagModel[]) at start of save:', fieldSelectionValue);

      const currentSelectedTagIds = fieldSelectionValue.map((tag) => tag.id);
      console.log('[WorklistTagFilterAccordionItem] Derived currentSelectedTagIds for filtering:', currentSelectedTagIds);

      let originalKendoState: State = {};
      if (worklist.dataState) {
        try {
          originalKendoState = JSON.parse(worklist.dataState);
        } catch (e) {
          console.error('Error parsing existing worklist.dataState:', e);
          originalKendoState = { filter: { logic: 'and', filters: [] } };
        }
      }

      if (!originalKendoState.filter || typeof originalKendoState.filter !== 'object') {
        originalKendoState.filter = { logic: 'and', filters: [] };
      } else {
        if (!Array.isArray(originalKendoState.filter.filters)) {
          originalKendoState.filter.filters = [];
        }
        if (typeof originalKendoState.filter.logic !== 'string') {
          originalKendoState.filter.logic = 'and';
        }
      }

      const baseFilters = (originalKendoState.filter?.filters || []).filter((f) => {
        if (typeof f === 'object' && f && 'field' in f && typeof f.field === 'string') {
          if (f.field === 'TagId') return false;
          if (patientId && f.field === 'PatientId') return false;
        }
        return true;
      });

      const activeFilters: (FilterDescriptor | CompositeFilterDescriptor)[] = [...baseFilters];

      if (currentSelectedTagIds.length > 0) {
        activeFilters.push({
          field: 'TagId',
          operator: 'in',
          value: currentSelectedTagIds,
        });
      }

      if (patientId) {
        activeFilters.push({
          field: 'PatientId',
          operator: 'eq',
          value: patientId,
        });
      }

      const newFilterDescriptor: CompositeFilterDescriptor | undefined =
        activeFilters.length > 0 ? { logic: originalKendoState.filter?.logic || 'and', filters: activeFilters } : undefined;

      const finalKendoState: State = {
        ...originalKendoState,
        filter: newFilterDescriptor,
      };
      console.log('[WorklistTagFilterAccordionItem] Final Kendo State to be saved:', JSON.stringify(finalKendoState));
      console.log('[WorklistTagFilterAccordionItem] oDataResourceString to be saved:', toDataSourceRequestString(finalKendoState));

      onUpdateWorklist([
        {
          ...worklist,
          name: worklistName,
          dataState: JSON.stringify(finalKendoState),
          columnState: JSON.stringify(columnsStateForm),
          oDataResourceString: toDataSourceRequestString(finalKendoState),
        },
      ]);
      toggleFiltersAccordionClick(event);
    });

    const handleSelfCreateWorklist = useEvent((name: string) => {
      onCreateWorklist(name, isTopLevel ? null : worklist.id);
    });

    const handleDeleteWorklistClick = useEvent(() => {
      onDeleteWorklist(worklist);
    });

    return (
      <div ref={ref} style={{ opacity: isDragging ? 0.4 : 1, position: 'relative' }}>
        {isOver && dropIndicator === 'above' && <StyledDropIndicator style={{ top: 0 }} />}
        <StyledHeaderWrapperDiv
          ref={headerRef}
          $isExpanded={isItemsExpanded || isFiltersExpanded}
          $isTopLevel={isTopLevel}
          $isDropTarget={isOver && dropIndicator === 'on'}
          $nestingLevel={nestingLevel}
        >
          {childWorklists.length > 0 &&
            (isItemsExpanded ? <StyledCollapseIcon onClick={toggleItemsAccordionClick} /> : <StyledExpandIcon onClick={toggleItemsAccordionClick} />)}
          {isFiltersExpanded ? (
            <StyledEditWorklistHeaderDiv>
              <StyledEditNameWorklistInput initialValue={worklistName} saveButtonText="Save Name" value={worklistName} onChange={setWorklistName} />
              <StyledSaveWorklistButton type="button" onClick={handleSaveClick}>
                Save
              </StyledSaveWorklistButton>
            </StyledEditWorklistHeaderDiv>
          ) : (
            <StyledHeaderDiv>
              <StyledNameDiv>{worklist.name}</StyledNameDiv>
              <StyledActionsDiv>
                <Button type="button" variant={ButtonVariants.SECONDARY} onClick={toggleFiltersAccordionClick}>
                  Edit
                </Button>
                <Button type="button" variant={ButtonVariants.SECONDARY} onClick={handleDeleteWorklistClick}>
                  Delete
                </Button>
              </StyledActionsDiv>
            </StyledHeaderDiv>
          )}
          <StyledHandleContainer ref={drag}>
            <StyledHandleIcon />
          </StyledHandleContainer>
        </StyledHeaderWrapperDiv>
        {isOver && dropIndicator === 'below' && <StyledDropIndicator style={{ bottom: 0 }} />}
        <StyledFiltersAccordionCollapse eventKey={`${worklist.id}-filters`}>
          <StyledBodyWrapperDiv>
            <StyledLabelDiv>TAGS</StyledLabelDiv>
            {isLoadingTags ? (
              <div>Loading tags...</div>
            ) : (
              <StyledTagsField
                tags={allHierarchicalTags}
                value={fieldSelectionValue}
                onChange={setFieldSelectionValue}
                label="Select tags for this worklist"
                placeholder="Search or select tags..."
              />
            )}
          </StyledBodyWrapperDiv>
        </StyledFiltersAccordionCollapse>
        <StyledItemsAccordionCollapse eventKey={`${worklist.id}-items`}>
          <>
            {childWorklists.map((childItem) => (
              <WorklistFilterAccordionItem
                key={childItem.id}
                allWorklists={allWorklists}
                worklist={childItem}
                currentDataState={currentDataState}
                currentColumnsState={currentColumnsState}
                onCreateWorklist={onCreateWorklist}
                onDeleteWorklist={onDeleteWorklist}
                onUpdateWorklist={onUpdateWorklist}
                filterType={childItem.entity === 'PatientFile' ? 'tag' : 'field'}
                patientId={patientId}
                dndType={dndType}
                nestingLevel={nestingLevel + 1}
              />
            ))}
            <StyledCreateWorklistNameInput onSave={handleSelfCreateWorklist} saveButtonText="Create Worklist" />
          </>
        </StyledItemsAccordionCollapse>
      </div>
    );
  },
);

WorklistTagFilterAccordionItem.displayName = 'WorklistTagFilterAccordionItem';

const StyledHeaderWrapperDiv = styled.div<{
  $isExpanded: boolean;
  $isTopLevel: boolean;
  $isDropTarget?: boolean;
  $nestingLevel: number;
}>`
  position: relative;
  align-items: center;
  display: flex;
  margin-left: ${({ $isTopLevel, theme, $nestingLevel }) => ($isTopLevel ? 0 : parseInt(theme.space.spacing60, 10) + $nestingLevel * 15)}px;
  margin-bottom: ${({ $isExpanded, theme }) => ($isExpanded ? 0 : theme.space.spacing30)};
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  ${({ $isDropTarget, theme }) =>
    $isDropTarget &&
    `
    outline: 2px solid ${theme.colors.primary};
    background-color: ${theme.colors.palette.blues[0]};
  `}
`;

const StyledCollapseIcon = styled(CollapseIcon)`
  cursor: pointer;
  margin-right: ${({ theme }) => theme.space.spacing30};
`;

const StyledExpandIcon = styled(ExpandIcon)`
  cursor: pointer;
  margin-right: ${({ theme }) => theme.space.spacing30};
`;

const StyledEditWorklistHeaderDiv = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  display: flex;
  width: 100%;
  padding-top: ${({ theme }) => theme.space.spacing20};
  padding-left: ${({ theme }) => theme.space.spacing20};
`;

const StyledEditNameWorklistInput = styled(WorklistNameInput)`
  & > input {
    background: white;
  }
`;

const StyledSaveWorklistButton = styled(Button)`
  margin-left: ${({ theme }) => theme.space.spacing50};
  margin-right: ${({ theme }) => theme.space.spacing20};
`;

const StyledHeaderDiv = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  border-radius: ${({ theme }) => theme.radii.base};
  color: ${({ theme }) => theme.colors.palette.grayscale[9]};
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.spacing30} ${({ theme }) => theme.space.spacing40};
  width: 100%;
`;

const StyledNameDiv = styled.div`
  flex: 1;
`;

const StyledActionsDiv = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledHandleContainer = styled.div`
  cursor: move;
  margin-left: ${({ theme }) => theme.space.spacing40};
  display: flex;
  align-items: center;
`;

const StyledHandleIcon = styled(OriginalHandleIcon)`
  fill: ${({ theme }) => theme.colors.palette.grayscale[7]};
`;

const StyledFiltersAccordionCollapse = styled(Accordion.Collapse)`
  background: ${({ theme }) => theme.colors.palette.grayscale[2]};
  color: ${({ theme }) => theme.colors.palette.grayscale[9]};
  margin-bottom: ${({ theme }) => theme.space.spacing30};
  margin-right: 27px;
  margin-left: ${({ theme }) => theme.space.spacing60};
`;

const StyledLabelDiv = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.space.spacing20};
  margin-top: ${({ theme }) => theme.space.spacing20};
`;

const StyledItemsAccordionCollapse = styled(Accordion.Collapse)`
  border-left: 1px solid ${({ theme }) => theme.colors.palette.grayscale[5]};
  margin: 0 0 0 6px;
  padding-top: ${({ theme }) => theme.space.spacing40};
  padding-bottom: ${({ theme }) => theme.space.spacing40};
  padding-right: 0;
  margin-left: ${({ theme }) => theme.space.spacing30};
`;

const StyledCreateWorklistNameInput = styled(WorklistNameInput)`
  margin-left: ${({ theme }) => theme.space.spacing60};
  margin-right: 27px;
`;

const StyledTagsField = styled(TagsField)`
  margin-bottom: ${({ theme }) => theme.space.spacing30};
`;

const StyledDropIndicator = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.primary};
  z-index: 1;
`;

const StyledBodyWrapperDiv = styled.div`
  padding: ${({ theme }) => theme.space.spacing40};
`;
